import { $log } from './debug';
import { $trigger, YEventHandlers, YInputEvent, YStateEvent, YValueEvent } from './events';
import { YInput } from './form';
import { yEqual } from './object';
import { YDate, YTime } from './time';
import { YId } from './types';
import { yMergeSorted, ySortInline, YSortSpec, ySortWithMapping } from './util';

export interface QValue {
    id?: YId;
    str?: string;
    int?: number;
    bool?: boolean;
    flt?: number;
    time?: YTime;
    date?: YDate;
}

export interface QCondition {
    or?: QCondition[];
    not?: QCondition;

    eq?: QValue;
    gt?: QValue;
    lt?: QValue;
    ge?: QValue;
    le?: QValue;
    ne?: QValue;
    like?: string;
    in?: QValue[];
    search?: string[];
}

export interface QByFilter {
    by: Record<string, QCondition>;
}

export interface QOrFilter {
    or: QFilter[];
}

export type QFilter = QByFilter | QOrFilter

export type QCursor = QValue[];

export interface QRequest {
    limit?: number;
    cursor?: QCursor;
    sort?: string[];
    filter?: QFilter;
    count?: boolean;
}

export interface QResponse<Model> {
    data: Model[];
    cursor?: QCursor;
    query_sql: string;
    t_query: number;
    count?: number;
    count_sql?: string;
    t_count?: number;
}

export interface YDataSourceObserver<Model> {
    onItemsInserted(indices: number[], source: YDataSource<Model>): void;
    onItemsAdded(index: number, count: number, source: YDataSource<Model>): void;
    onItemsRemoved(index: number, count: number, source: YDataSource<Model>): void;
    onItemUpdated(index: number, source: YDataSource<Model>): void;
    onItemMoved(from: number, to: number, source: YDataSource<Model>): void;
    onItemsChecked?(source: YDataSource<Model>): void;
    onItemsSorted?(mapping: number[], source: YDataSource<Model>): void;
    onItemsWillRead?(source: YDataSource<Model>): void;
    onItemsChanged(source: YDataSource<Model>): void;
}

// TODOURGENT: @$y.LifetimeTracked 
export abstract class YDataSource<Model> {
    private _observers: YDataSourceObserver<Model>[];

    constructor() {
        this._observers = [];
    }

    addObserver(o: YDataSourceObserver<Model>) {
        this._observers.push(o);
    }

    removeObserver(o: YDataSourceObserver<Model>) {
        const i = this._observers.indexOf(o);
        if(i >= 0)
            this._observers.splice(i, 1);
    }

    get can_load_more(): boolean {
        return false;
    }

    sortBySpec?(spec: YSortSpec<Model>): void;
    sortById?(id: string, o: number): void;

    loadMore() {
    }

    checkAll?(_check: boolean): void;
    checkIf?(_predicate: (item: Model) => boolean): void;
    check?(_item: Model, checked: boolean): void;
    isChecked?(item: Model): boolean;

    protected notifyItemsAdded(index: number, count: number) {
        for(const o of this._observers) {
            o.onItemsAdded(index, count, this);
        }
    }

    protected notifyItemsInserted(indices: number[]) {
        for(const o of this._observers) {
            o.onItemsInserted(indices, this);
        }
    }

    protected notifyItemsRemoved(index: number, count: number) {
        for(const o of this._observers) {
            o.onItemsRemoved(index, count, this);
        }
    }

    protected notifyItemUpdated(index: number) {
        for(const o of this._observers) {
            o.onItemUpdated(index, this);
        }
    }

    protected notifyItemMoved(from: number, to: number) {
        for(const o of this._observers) {
            o.onItemMoved(from, to, this);
        }
    }

    protected notifyItemsSorted(mapping: number[]) {
        for(const o of this._observers) {
            if(o.onItemsSorted)
                o.onItemsSorted(mapping, this);
            else
                o.onItemsChanged(this);
        }
    }

    protected notifyItemsChanged() {
        for(const o of this._observers) {
            o.onItemsChanged(this);
        }
    }

    protected notifyItemsChecked() {
        for(const o of this._observers) {
            o.onItemsChecked?.(this);
        }
    }

    protected notifyItemsWillRead() {
        for(const o of this._observers) {
            o.onItemsWillRead?.(this);
        }
    }

    abstract onItemMoved(from: number, to: number): void;
    abstract at(index: number): Model;
    abstract count: number;
}

export class YArrayDataSource<Model extends object> extends YDataSource<Model> {
    $handlers: YEventHandlers<{ checked: void }>;

    private _checked?: Set<Model>;
    private _all: Model[];
    private _data: Model[];
    private _filter?: (item: Model) => boolean;
    private _sort?: YSortSpec<Model>;

    constructor(data: Model[] = []) {
        super();
        this._data = this._all = data;
    }

    override sortBySpec(spec: YSortSpec<Model>) {
        if(this._all != this._data)
            ySortInline(this._all, spec);

        if(this._data.length) {
            const [mapping, data] = ySortWithMapping(this._data, spec);
            this._data = data;
            this.notifyItemsSorted(mapping);
        }

        this._sort = spec;
    }

    override check(item: Model, check: boolean): void {
        if(this._checked) {
            if(check) {
                if(this._checked.has(item)) return;

                this._checked.add(item);
            } else {
                if(!this._checked.has(item)) return;

                this._checked.delete(item);
            }
        } else {
            if(check) {
                this._checked = new Set([item]);
            } else {
                return;
            }
        }

        this.notifyItemsChecked();
        $trigger(this, 'checked');
    }

    override isChecked(item: Model): boolean {
        return !!this._checked?.has(item);
    }

    override checkIf(predicate: (item: Model) => boolean) {
        this._checked = new Set(this._data.filter(predicate));
        this.notifyItemsChecked();
        $trigger(this, 'checked');
    }

    override checkAll(check: boolean) {
        this._checked = check ? new Set(this._data) : undefined;
        this.notifyItemsChecked();
        $trigger(this, 'checked');
    }

    get checked_count() {
        return this._checked?.size || 0;
    }

    get checked_models(): Model[] {
        return this._checked ? Array.from(this._checked) : [];
    }

    reload() {
        //resorts and refilters
        if(this._sort)
            ySortInline(this._all, this._sort);

        this._data = this._filter ? this._all.filter(this._filter) : this._all;
        this.notifyItemsChanged();
    }

    get data(): readonly Model[] {
        return this._all;
    }

    set data(r_data: readonly Model[]) {
        const data = Array.from(r_data);
        if(this._sort)
            ySortInline(data, this._sort);

        if(this._checked) {
            const checked = new Set<Model>();
            for(const item of data) {
                if(this._checked.has(item))
                    checked.add(item);
            }
            this._checked = checked;
        }

        this._all = data;
        this._data = this._filter ? data.filter(this._filter) : data;
        this.notifyItemsChanged();
    }

    get filter(): ((item: Model) => boolean) | undefined {
        return this._filter;
    }

    set filter(filter: ((item: Model) => boolean) | undefined) {
        if(this._filter == filter) return;

        this._filter = filter;
        this._data = filter ? this._all.filter(filter) : this._all;
        this.notifyItemsChanged();
    }

    remove(item: Model) {
        if(this._all != this._data) {
            const i = this._all.indexOf(item);
            if(i < 0) return;
            this._all.splice(i, 1);
        }

        this._removeIndex(this._data.indexOf(item));
    }

    private _removeIndex(i: number) {
        if(i < 0 || i >= this._data.length) return;
        this._data.splice(i, 1);
        this.notifyItemsRemoved(i, 1);
    }

    add(...items: Model[]) {
        if(this._sort) {
            ySortInline(items, this._sort);

            if(this._filter) {
                this._all = yMergeSorted(this._all, items, this._sort)[0];
                items = items.filter(this._filter);
            }

            const [data, positions] = yMergeSorted(this._data, items, this._sort);
            this._data = data;
            $log('app', 'merged', positions);
            this.notifyItemsInserted(positions);
        } else {
            if(this._filter) {
                this._all.push(...items);
                items = items.filter(this._filter);
            }

            this._data.push(...items);
            this.notifyItemsAdded(this._data.length - items.length, items.length);
        }
    }

    onItemMoved(from: number, to: number) {
        this._data.splice(to, 0, this._data.splice(from, 1)[0]);
        this.notifyItemMoved(from, to);
    }

    override at(index: number): Model {
        return this._data[index];
    }

    override get count(): number {
        return this._data.length;
    }
}

export class YInputDataSource<Model> extends YDataSource<Model> implements YInput<Model[]> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<Model[]>>;

    private _data: Model[];
    private _sort?: YSortSpec<Model>;

    constructor(data: Model[] = []) {
        super();
        this._data = data;
    }

    override sortBySpec(spec: YSortSpec<Model>) {
        if(this._data.length) {
            const [mapping, data] = ySortWithMapping(this._data, spec);
            this._data = data;
            this.notifyItemsSorted(mapping);
        }
        this._sort = spec;
    }

    get value(): Model[] {
        this.notifyItemsWillRead();
        return this._data;
    }

    set value(data: Model[]) {
        data = Array.from(data);
        if(this._sort) {
            const comparator = this._sort[0];
            const o = this._sort[1];
            data.sort((a, b) => comparator(a, b, o));
        }
        this._data = data;
        this.notifyItemsChanged();
    }

    remove(item: Model) {
        const i = this._data.indexOf(item);
        if(i >= 0)
            this.removeIndex(i);
    }

    removeIndex(i: number) {
        this._data.splice(i, 1);
        this.notifyItemsRemoved(i, 1);
    }

    insertBefore(item: Model, ref: Model) {
        const i = this._data.indexOf(ref)
        if(i >= 0) {
            this._data.splice(i, 0, item)
            this.notifyItemsInserted([i]);
        }
    }

    push(...items: Model[]) {
        this._data.push(...items);
        this.notifyItemsAdded(this._data.length - items.length, items.length);
    }

    onItemMoved(from: number, to: number) {
        this._data.splice(to, 0, this._data.splice(from, 1)[0]);
        this.notifyItemMoved(from, to);
    }

    override at(index: number): Model {
        return this._data[index];
    }

    override get count(): number {
        return this._data.length;
    }

    get okay() {
        return true;
    }

    get dirty() {
        return true;
    }

    get disabled() {
        return false;
    }
}

type YDoQueryFunction<Model> = (params: QRequest) => Promise<QResponse<Model>>;

function printQueryStats(response: QResponse<any>) {
    $log('app', 'SQL', response.query_sql);
    $log('app', 'query time', response.t_query);
    if(response.count != undefined) {
        $log('app', 'SQL count', response.count_sql);
        $log('app', 'count time', response.t_count);
    }
}

export class YQueryDataSource<Model> extends YDataSource<Model> {
    private _data: Model[];
    private _pending?: {};
    private _exhausted?: boolean;
    private _failed?: boolean;
    private _sort?: [string, number][];
    private _filter?: QFilter;
    private _cursor?: QCursor;
    private _do_query: YDoQueryFunction<Model>;
    private _need_count: boolean;
    private _notify_changed?: boolean;

    constructor(do_query: YDoQueryFunction<Model>) {
        super();
        this._data = [];
        this._need_count = true;
        this._do_query = do_query;
    }

    override get can_load_more() {
        return !this._pending && !this._exhausted && !this._failed;
    }

    override loadMore() {
        $log('table', 'loading more');
        if(this._pending) return;
        $log('table', 'really loading more');
        const token = this._pending = {};
        const sort = this._sort?.map(s => ((s[1] > 0 ? '+' : '-') + s[0]));
        this._do_query({ limit: 100, cursor: this._cursor, sort, filter: this._filter, count: this._need_count }).then(response => {
            printQueryStats(response);

            if(this._pending === token) {
                this._need_count = false;
                this._pending = undefined;
                const models = response.data;
                if(models.length < 100) {
                    this._exhausted = true;
                } else {
                    this._cursor = response.cursor;
                }
                this._data.push(...models);

                if(this._notify_changed) {
                    this.notifyItemsChanged();
                    this._notify_changed = false;
                } else if(models.length) {
                    this.notifyItemsAdded(this._data.length - models.length, models.length);
                }
            }
        }, () => {
            if(this._pending === token) {
                this._pending = undefined;
                this._failed = true;
            }
        });
    }

    reload() {
        if(this._data.length || this._exhausted || this._failed) {
            this._data = [];
            this._cursor = undefined;
            this._pending = undefined;
            this._exhausted = false;
            this.loadMore();
            this._notify_changed = true;
        }
    }

    refresh() {
        this._need_count = true;
        this.reload();
    }

    set filter(filter: QFilter | undefined) {
        if(this._filter == filter || yEqual(this._filter, filter)) return;

        this._filter = filter;
        this._need_count = true;
        this.reload();
    }

    override sortById(id: string, o: number): void {
        $log('table', 'sorting by', id, o);
        if(this._sort?.length && this._sort[0][0] == id && this._sort[0][1] == o) return;
        this._sort = [[id, o]];
        this.reload();
    }

    override onItemMoved(_from: number, _to: number) {
        // TODO
    }

    override at(index: number): Model {
        return this._data[index];
    }

    override get count(): number {
        return this._data.length;
    }
}
